import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HeroContainer = styled.section`
  min-height: 100vh;
  min-height: calc(100dvh - 80px);
  display: flex;
  align-items: center;
  position: relative;
  background: linear-gradient(135deg, var(--primary) 0%, #333333 100%);
  overflow: hidden;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    min-height: calc(100dvh - 70px);
  }
  
  .dark-mode & {
    background: linear-gradient(135deg, #111111 0%, #000000 100%);
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  color: var(--white);
  position: relative;
  z-index: 2;
  width: 100%;
  box-sizing: border-box;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.1;
  
  span {
    display: block;
  }
  
  .highlight {
    color: var(--secondary);
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0.2rem;
      left: 0;
      width: 100%;
      height: 0.5rem;
      background-color: var(--accent);
      z-index: -1;
      opacity: 0.3;
    }
  }
  
  @media (max-width: 992px) {
    font-size: 3.8rem;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
    margin-bottom: 1.2rem;
  }
  
  @media (max-width: 576px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  margin-bottom: 2.5rem;
  max-width: 600px;
  color: #ffffff !important;
  line-height: 1.6;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 1.8rem;
  }
`;

const ButtonContainer = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  
  @media (max-width: 576px) {
    flex-direction: column;
    width: 100%;
    gap: 1rem;
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--secondary);
  color: var(--black);
  padding: 1rem 2.5rem;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  transition: all var(--transition-fast);
  position: relative;
  overflow: hidden;
  border: none;
  cursor: pointer;
  
  @media (max-width: 768px) {
    padding: 0.9rem 2rem;
  }
  
  @media (max-width: 576px) {
    width: 100%;
    padding: 0.9rem 1.5rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transform: translateX(-100%);
  }
  
  &:hover {
    background-color: var(--accent);
    color: var(--white);
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    
    &::before {
      transform: translateX(100%);
      transition: transform 0.8s ease;
    }
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  color: var(--white);
  padding: 1rem 2.5rem;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  border: 2px solid var(--white);
  transition: all var(--transition-fast);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  
  @media (max-width: 768px) {
    padding: 0.9rem 2rem;
  }
  
  @media (max-width: 576px) {
    width: 100%;
    padding: 0.9rem 1.5rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--white);
    transform: scaleX(0);
    transform-origin: right;
    transition: transform var(--transition-fast);
    z-index: -1;
  }
  
  &:hover {
    color: var(--black);
    border-color: var(--white);
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    
    &::before {
      transform: scaleX(1);
      transform-origin: left;
    }
  }
`;

const BackgroundShapes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
`;

const Shape = styled(motion.div)`
  position: absolute;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: ${props => props.type === 'circle' ? '50%' : '0'};
  
  ${props => props.type === 'square' && `
    transform: rotate(45deg);
  `}
`;

const BelgianFlag = styled(motion.div)`
  position: absolute;
  width: 60px;
  height: 40px;
  display: flex;
  flex-direction: row;
  opacity: 0.6;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  z-index: 10;
  
  div {
    flex: 1;
  }
  
  .black {
    background-color: #000000;
  }
  
  .yellow {
    background-color: #FDDA24;
  }
  
  .red {
    background-color: #EF3340;
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--white);
  opacity: 0.7;
  
  .mouse {
    width: 30px;
    height: 50px;
    border: 2px solid var(--white);
    border-radius: 20px;
    display: flex;
    justify-content: center;
    margin-bottom: 0.5rem;
  }
  
  .wheel {
    width: 4px;
    height: 8px;
    background-color: var(--white);
    border-radius: 2px;
    margin-top: 8px;
    animation: scroll 1.5s ease infinite;
  }
  
  span {
    font-size: 0.8rem;
    letter-spacing: 1px;
  }
  
  @keyframes scroll {
    0% {
      transform: translateY(0);
      opacity: 1;
    }
    100% {
      transform: translateY(20px);
      opacity: 0;
    }
  }
`;

const HeroSection = () => {
  const containerRef = useRef(null);
  const [flagPosition, setFlagPosition] = useState({ x: 50, y: 50 });
  const [flagVelocity, setFlagVelocity] = useState({ vx: 2, vy: 1.5 });
  
  const FLAG_WIDTH = 60;
  const FLAG_HEIGHT = 40;

  useEffect(() => {
    let animationFrameId;
    
    const animate = () => {
      if (!containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const maxX = containerRect.width - FLAG_WIDTH - 10;
      const maxY = containerRect.height - FLAG_HEIGHT - 10;
      const minY = 10; // Petit offset du haut
      
      setFlagPosition(prev => {
        let newX = prev.x + flagVelocity.vx;
        let newY = prev.y + flagVelocity.vy;
        let newVx = flagVelocity.vx;
        let newVy = flagVelocity.vy;
        
        // Rebond sur les bords horizontaux
        if (newX <= 10 || newX >= maxX) {
          newVx = -newVx;
          newX = newX <= 10 ? 10 : maxX;
        }
        
        // Rebond sur les bords verticaux
        if (newY <= minY || newY >= maxY) {
          newVy = -newVy;
          newY = newY <= minY ? minY : maxY;
        }
        
        // Mettre à jour la vélocité si elle a changé
        if (newVx !== flagVelocity.vx || newVy !== flagVelocity.vy) {
          setFlagVelocity({ vx: newVx, vy: newVy });
        }
        
        return { x: newX, y: newY };
      });
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animationFrameId = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [flagVelocity]);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroContainer id="accueil" ref={containerRef}>
      <BackgroundShapes>
        <Shape
          type="circle"
          initial={{ x: '-10%', y: '10%', opacity: 0 }}
          animate={{ x: '0%', y: '0%', opacity: 1 }}
          transition={{ duration: 10, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          style={{ width: '300px', height: '300px', top: '10%', left: '10%' }}
        />
        <Shape
          type="square"
          initial={{ x: '10%', y: '-10%', opacity: 0 }}
          animate={{ x: '0%', y: '0%', opacity: 1 }}
          transition={{ duration: 12, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          style={{ width: '200px', height: '200px', bottom: '20%', right: '30%' }}
        />
        <Shape
          type="circle"
          initial={{ x: '-5%', y: '5%', opacity: 0 }}
          animate={{ x: '5%', y: '-5%', opacity: 1 }}
          transition={{ duration: 8, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          style={{ width: '150px', height: '150px', bottom: '10%', left: '30%' }}
        />
        <Shape
          type="square"
          initial={{ rotate: 0, opacity: 0 }}
          animate={{ rotate: 360, opacity: 1 }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          style={{ width: '100px', height: '100px', top: '30%', right: '10%' }}
        />
      </BackgroundShapes>
      
      <BelgianFlag
        style={{
          left: flagPosition.x,
          top: flagPosition.y,
        }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 0.6, scale: 1 }}
        transition={{ duration: 0.5, delay: 1 }}
      >
        <div className="black"></div>
        <div className="yellow"></div>
        <div className="red"></div>
      </BelgianFlag>
      
      <HeroContent>
        <HeroTitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span>Votre projet,</span>
          <span>mon <span className="highlight">expertise</span></span>
        </HeroTitle>
        
        <ButtonContainer
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <PrimaryButton onClick={() => scrollToSection('devis')}>Estimez votre projet</PrimaryButton>
          <SecondaryButton onClick={() => scrollToSection('contact')}>Me contacter</SecondaryButton>
        </ButtonContainer>
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection;
